import { useEffect, useState } from 'react';
import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { copyToClipboard } from '../../../../utils/clipboard';
import { NEUTRAL_BUTTON_CLASS } from '../details/CredsActionButtons';

const COPIED_RESET_MS = 1500;

type CopyStatus = 'idle' | 'copied' | 'failed';

interface CopyButtonProps {
    text: string;
    /** What is being copied, e.g. "patient ID"; names the icon-only button. */
    label: string;
    /** Shows a visible caption beside the icon instead of an icon-only button. */
    caption?: string;
}

export function CopyButton({ text, label, caption }: CopyButtonProps) {
    const [status, setStatus] = useState<CopyStatus>('idle');

    useEffect(() => {
        if (status === 'idle') return;
        const timer = setTimeout(() => setStatus('idle'), COPIED_RESET_MS);
        return () => clearTimeout(timer);
    }, [status]);

    const handleCopy = async () => {
        setStatus((await copyToClipboard(text)) ? 'copied' : 'failed');
    };

    const Icon = status === 'copied' ? CheckIcon : ClipboardDocumentIcon;
    const announcement =
        status === 'copied' ? 'Copied' : status === 'failed' ? "Couldn't copy" : '';
    const iconOnlyClass =
        'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400';

    return (
        <>
            <button
                type="button"
                onClick={handleCopy}
                aria-label={caption ? undefined : `Copy ${label}`}
                className={caption ? NEUTRAL_BUTTON_CLASS : iconOnlyClass}
            >
                <Icon
                    className={`h-3.5 w-3.5 ${status === 'copied' ? 'text-green-600' : ''}`}
                    aria-hidden="true"
                />
                {caption && (status === 'copied' ? 'Copied' : caption)}
            </button>
            <span className="sr-only" aria-live="polite">
                {announcement}
            </span>
        </>
    );
}
